import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useThemeContext } from '../contexts/ThemeContext';
import api from '../services/api';

function Bloqueio() {
    const navigate = useNavigate();
    const location = useLocation();
    const { themeMode, colors } = useThemeContext();
    const [motivo, setMotivo] = useState('Sessão bloqueada por inatividade');
    const [permiteLogin, setPermiteLogin] = useState(true);

    useEffect(() => {
        // Verifica o motivo do bloqueio (pode vir de state ou query params)
        const state = location.state || {};
        const searchParams = new URLSearchParams(location.search);
        const motivoParam = searchParams.get('motivo') || state.motivo;
        
        if (motivoParam === 'horario_bloqueado' || state.motivo === 'horario_bloqueado') {
            const horaInicio = searchParams.get('hora_inicio') || state.hora_inicio || '07:00';
            const horaFim = searchParams.get('hora_fim') || state.hora_fim || '18:00';
            const horaAtual = searchParams.get('hora_atual') || state.hora_atual || '';
            
            setMotivo(`Acesso bloqueado fora do horário permitido (${horaInicio} - ${horaFim})${horaAtual ? `. Hora atual: ${horaAtual}` : ''}`);
            setPermiteLogin(false);
        } else if (motivoParam === 'inatividade' || state.motivo === 'inatividade') {
            setMotivo('Sessão bloqueada por inatividade');
            setPermiteLogin(true);
        }

        // Limpa token se houver
        if (state.limparToken) {
            localStorage.removeItem('token');
        }
    }, [location]);

    const handleLogin = () => {
        if (permiteLogin) {
            navigate('/login');
        }
    };

    // Estilos baseados no tema
    const isDark = themeMode === 'dark';
    const primaryColor = colors.primary || (isDark ? '#3e2723' : '#8d6e63');
    const bgGradient = isDark 
        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)'
        : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 50%, #d5d5d5 100%)';
    const textColor = isDark ? '#ffffff' : '#333333';
    const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)';

    return (
        <Container 
            maxWidth={false} 
            sx={{ 
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
                position: 'relative',
                overflow: 'hidden',
                background: bgGradient
            }}
        >
            {/* Background decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    opacity: isDark ? 0.1 : 0.05
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '80px',
                        left: '40px',
                        width: '256px',
                        height: '256px',
                        borderRadius: '50%',
                        background: primaryColor,
                        filter: 'blur(80px)'
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '80px',
                        right: '40px',
                        width: '384px',
                        height: '384px',
                        borderRadius: '50%',
                        background: primaryColor,
                        filter: 'blur(100px)'
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '600px',
                        height: '600px',
                        borderRadius: '50%',
                        background: primaryColor,
                        filter: 'blur(120px)'
                    }}
                />
            </Box>

            {/* Main card */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 10,
                    width: '100%',
                    maxWidth: '448px',
                    px: 2
                }}
            >
                <Box
                    sx={{
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        borderRadius: '24px',
                        p: { xs: 4, md: 5 },
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        background: isDark 
                            ? 'rgba(255,255,255,0.05)' 
                            : 'rgba(255,255,255,0.8)'
                    }}
                >
                    {/* Lock icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 4,
                            animation: 'float 4s ease-in-out infinite',
                            '@keyframes float': {
                                '0%, 100%': { transform: 'translateY(0px)' },
                                '50%': { transform: 'translateY(-10px)' }
                            }
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50%',
                                    animation: 'pulse-ring 3s ease-in-out infinite',
                                    background: `${primaryColor}30`,
                                    transform: 'scale(1.5)',
                                    '@keyframes pulse-ring': {
                                        '0%': { transform: 'scale(0.95)', opacity: 0.5 },
                                        '50%': { transform: 'scale(1.05)', opacity: 0.3 },
                                        '100%': { transform: 'scale(0.95)', opacity: 0.5 }
                                    }
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'relative',
                                    animation: 'float 4s ease-in-out infinite'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '96px',
                                        height: '96px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
                                    }}
                                >
                                    <LockIcon sx={{ fontSize: '48px', color: 'white' }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* System name */}
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            color: textColor,
                            mb: 1,
                            fontFamily: '"Playfair Display", serif'
                        }}
                    >
                        Sistema Jurídico
                    </Typography>

                    {/* Lock message */}
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: textSecondary,
                            mb: 4,
                            fontFamily: '"Inter", sans-serif'
                        }}
                    >
                        {motivo}
                    </Typography>

                    {/* Divider */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 4
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                height: '1px',
                                background: `linear-gradient(to right, transparent, ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'})`
                            }}
                        />
                        <Box
                            component="svg"
                            sx={{ width: '20px', height: '20px', color: textSecondary }}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                height: '1px',
                                background: `linear-gradient(to left, transparent, ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'})`
                            }}
                        />
                    </Box>

                    {/* Login button */}
                    {permiteLogin && (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleLogin}
                            sx={{
                                py: 2,
                                px: 3,
                                borderRadius: '12px',
                                fontSize: '18px',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontFamily: '"Inter", sans-serif',
                                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                color: 'white',
                                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.3)'
                                },
                                '&:active': {
                                    transform: 'scale(0.98)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Fazer Login
                        </Button>
                    )}

                    {!permiteLogin && (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                background: isDark ? 'rgba(255,0,0,0.1)' : 'rgba(255,0,0,0.05)',
                                border: `1px solid ${isDark ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.2)'}`
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                    color: textColor,
                                    fontFamily: '"Inter", sans-serif'
                                }}
                            >
                                O acesso ao sistema está bloqueado fora do horário permitido.
                                Entre em contato com o administrador para mais informações.
                            </Typography>
                        </Box>
                    )}

                    {/* Footer info */}
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: textSecondary,
                                fontSize: '14px',
                                fontFamily: '"Inter", sans-serif'
                            }}
                        >
                            {permiteLogin ? 'Clique acima para acessar o sistema' : 'Acesso restrito por horário'}
                        </Typography>
                    </Box>
                </Box>

                {/* Bottom branding */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            color: textSecondary
                        }}
                    >
                        <Box
                            component="svg"
                            sx={{ width: '16px', height: '16px' }}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '12px',
                                letterSpacing: '0.1em',
                                fontFamily: '"Inter", sans-serif'
                            }}
                        >
                            ACESSO SEGURO
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
}

export default Bloqueio;
